import { hasPermission, PERMISSIONS, type Permission } from '@repo/contracts';
import { Button, cn } from '@repo/ui';
import { LogOut } from 'lucide-react';
import { NavLink } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { selectPermissions } from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

interface MobileNavProps {
  onSignOut: () => void;
}

const LINKS: { to: string; label: string; end: boolean; permission?: Permission }[] = [
  { to: APP_ROUTES.home, label: 'Training', end: true },
  { to: APP_ROUTES.certificates, label: 'My certificates', end: false },
  {
    to: APP_ROUTES.questionnaires,
    label: 'Questionnaires',
    end: false,
    permission: PERMISSIONS.questionnaireRead,
  },
  {
    to: APP_ROUTES.assignments,
    label: 'Assignments',
    end: false,
    permission: PERMISSIONS.trainingAssignmentRead,
  },
  {
    to: APP_ROUTES.review,
    label: 'Review',
    end: false,
    permission: PERMISSIONS.trainingAttemptApprove,
  },
  {
    to: APP_ROUTES.allCertificates,
    label: 'Certificates',
    end: false,
    permission: PERMISSIONS.certificateRead,
  },
  {
    to: APP_ROUTES.groups,
    label: 'Groups',
    end: false,
    permission: PERMISSIONS.trainingGroupRead,
  },
  {
    to: APP_ROUTES.departments,
    label: 'Departments',
    end: false,
    permission: PERMISSIONS.trainingGroupRead,
  },
  {
    to: APP_ROUTES.settings,
    label: 'Settings',
    end: false,
    permission: PERMISSIONS.trainingSettingsRead,
  },
];

/** The shared sidebar is hidden below the lg breakpoint, so a phone gets this strip, which scrolls sideways so every link is reachable. */
export function MobileNav({ onSignOut }: MobileNavProps) {
  const permissions = useAppSelector(selectPermissions);
  const links = LINKS.filter(
    (link) => !link.permission || hasPermission(permissions, [link.permission]),
  );

  return (
    <nav
      aria-label="Training"
      className="-mx-5 -mt-7 mb-4 flex shrink-0 items-center gap-1 overflow-x-auto border-b border-border bg-card px-3 py-2 sm:-mx-8 sm:px-6 lg:hidden"
    >
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.end}
          className={({ isActive }) =>
            cn(
              'shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground',
              isActive && 'bg-primary-soft text-primary',
            )
          }
        >
          {link.label}
        </NavLink>
      ))}

      <Button variant="ghost" size="sm" className="ml-auto shrink-0" onClick={onSignOut}>
        <LogOut className="size-4" aria-hidden />
        Sign out
      </Button>
    </nav>
  );
}
